import { ApiError, CenteredLoader, Flex } from "@shared/primitive-components"
import { Stat, StatFeedback, SummaryCardMainText, getFeedbackProps } from ".."
import { useInsights } from "../../../contexts"
import { rebatesStyles as styles } from "../../styles"
import { usePolicyAccessCapabilities } from "../../../hooks"
import { useRebateSummary } from "../../../api/rebates/useGetRebateSummary"

export const MILEAGE_SUMMARY_DTI = {
  STAT: `mileage_summary_stat`,
  LABEL: `mileage_summary_label_stat`,
  FEEDBACK_ICON: `mileage_summary_feedback_icon`,
  FEEDBACK_TEXT: `mileage_summary_feedback_text`,
}

const MileageSummary = () => {
  const { isRebateV2Policy } = usePolicyAccessCapabilities()
  const { apiErrored: errRebates, data: summary, loading: rebatesLoading } = useRebateSummary()
  const { fleetEntriesProviderLoading, v2AccumulatedMileage } = useInsights()
  if (errRebates) return <ApiError />

  const hasData = isRebateV2Policy
    ? typeof v2AccumulatedMileage === `number` && v2AccumulatedMileage > 0
    : typeof summary?.accumulatedMileage === `number`
  const hasThreshold = isRebateV2Policy ? false : typeof summary?.accumulatedExpectedMileage === `number`
  const mileageEqualsThreshold =
    hasData && hasThreshold
      ? (summary?.accumulatedMileage as number) === (summary?.accumulatedExpectedMileage as number)
      : false
  const mileageBelowThreshold =
    hasData && hasThreshold
      ? (summary?.accumulatedMileage as number) <= (summary?.accumulatedExpectedMileage as number)
      : false
  const feedback = mileageEqualsThreshold
    ? `Your mileage is exactly as estimated`
    : `Your mileage is ${mileageBelowThreshold ? `lower` : `higher`} than estimated`

  return (
    <Flex flexDirection="column" justifyContent="space-between" height="100%">
      <SummaryCardMainText
        description={
          !rebatesLoading && !fleetEntriesProviderLoading
            ? isRebateV2Policy
              ? `This chart shows your fleet's mileage, see below for mileage per vehicle.`
              : `This chart shows your fleet's actual mileage compared to the estimate you gave us at the start of your policy. See below for mileage per vehicle.`
            : ``
        }
        main="Total fleet mileage"
        {...(!isRebateV2Policy && {
          target:
            !rebatesLoading && hasThreshold
              ? `Estimate: <${(summary?.accumulatedExpectedMileage as number).toLocaleString()}mi`
              : `Estimate: no data`,
        })}
      />
      {rebatesLoading || fleetEntriesProviderLoading ? (
        <>
          <CenteredLoader top="-6%" />
          <div />
        </>
      ) : (
        <Flex alignItems="end" css={styles.marginBottom(4)}>
          {hasData && (
            <Stat
              stat={
                isRebateV2Policy && typeof v2AccumulatedMileage === `number`
                  ? v2AccumulatedMileage.toLocaleString()
                  : !isRebateV2Policy && typeof summary?.accumulatedMileage === `number`
                  ? summary.accumulatedMileage.toLocaleString()
                  : ``
              }
              statLabel="mi"
              dataTestIds={{ stat: MILEAGE_SUMMARY_DTI.STAT, label: MILEAGE_SUMMARY_DTI.LABEL }}
            />
          )}
          {!isRebateV2Policy && (
            <StatFeedback
              variant="summary"
              feedback={getFeedbackProps(hasData && hasThreshold, mileageBelowThreshold, feedback, true)}
              dataTestIds={{
                icon: MILEAGE_SUMMARY_DTI.FEEDBACK_ICON,
                text: MILEAGE_SUMMARY_DTI.FEEDBACK_TEXT,
              }}
            />
          )}
        </Flex>
      )}
    </Flex>
  )
}

export const MileageSummaryComponent = <MileageSummary />
